import { Component, h, Prop, Element, State, Host, Listen } from '@stencil/core';
import {
  Logger,
  SectionBehavior,
  TextPosition,
  VerticalPositionBottom,
  VerticalPositionTop,
} from '../../global/_types';
import {
  getSectionBehavior, isSmallerThanDesktopViewport, isMobile, renderBlockMargin, elementIsVisibleInViewport,
} from '../../global/_utils';
import { colorDefs } from '../../global/_constants';

@Component({
    tag: 'cms-horizontal-tiles',
    styleUrl: 'cms-horizontal-tiles.scss',
    shadow: true
})
export class CmsHorizontalTiles {
  @Element() host: HTMLDivElement;

  @Prop() config: string;
  @Prop() debug: boolean = false;
  @Prop() behavior: string = '{}';

  @State() top: number = 0;
  @State() isMobile: boolean;

  private logger: Logger;

  private static ITEM_GAP: number = 20;
  private static TEXT_COLOR = colorDefs.font.main;
  private static BACKGROUND_COLOR = colorDefs.background.greyAccent;

  /* Config */
  private options: CmsHorizontalTilesConfig;

  private offset: number = 0;
  private lastOffset: number = 0;
  private list: HTMLUListElement;
  private buttonPrev: HTMLButtonElement;
  private buttonNext: HTMLButtonElement;

  private maxOffset: number;
  private itemWidth: number;
  private containerWidth: number;
  private x0: number;
  private y0: number;
  private container: HTMLDivElement;
  private buttonContainer: HTMLDivElement;
  private tilesContainer: HTMLDivElement;
  private tilesInner: HTMLDivElement;

  /* SectionBehavior */
  private sectionBehavior: SectionBehavior;

  componentWillLoad() {
    this.logger = new Logger(this.debug, 'CmsHorizontalTiles');

    // Config
    this.options = JSON.parse(this.config) as CmsHorizontalTilesConfig;

    // Calculations
    this.offset = 0;
    this.lastOffset = 0;

    // Section-Behavior
    this.sectionBehavior = getSectionBehavior(this.behavior);

    this.updateViewport();
  }

  componentDidLoad() {
    this.logger.trace('CmsHorizontalTiles loaded');

    this.itemWidth = this.container.querySelector('.card').getClientRects()[0].width + CmsHorizontalTiles.ITEM_GAP;
    this.maxOffset = (this.list.scrollWidth - this.list.clientWidth) * -1;
    this.containerWidth = this.options.items?.length * this.itemWidth - CmsHorizontalTiles.ITEM_GAP;

    // full width slider with more than 3 tiles
    if (this.options.items?.length > 3) {
      this.tilesContainer.classList.add('tiles-oversize');
      this.resizeContainer();
    } else {
      this.buttonContainer.classList.add('hidden');
    }
  }

  resizeContainer() {
    if (this.options.items?.length <= 3) {
      return;
    }

    if (!this.tilesInner || !this.buttonContainer) {
      return;
    }

    if (this.containerWidth <= window.innerWidth) {
      this.tilesInner.style.width = this.containerWidth + 'px';
      this.buttonContainer.classList.add('hidden');
    } else {
      this.tilesInner.style.width = null;
      this.buttonContainer.classList.remove('hidden');
    }
  }

  toggleCardScrollbar(overlay: Element = null) {
    function toggleScrollbar(content: Element) {
      const card = content.parentElement;
      const cardContainer = card.parentElement.parentElement as Element;
      const overflowClass = 'is-overflow';
      if (!elementIsVisibleInViewport(content, cardContainer, false)) {
        card.classList.add(overflowClass);
      } else {
        card.classList.remove(overflowClass);
      }
    }

    const contentSelector = '.card-overlay-content-wrapper';
    if (overlay) {
      const cardContent = overlay.querySelector(contentSelector);
      toggleScrollbar(cardContent);
    } else {
      this.host.shadowRoot.querySelectorAll(contentSelector).forEach((card: Element) => {
        toggleScrollbar(card);
      });
    }
  }

  // Resize EventHandler
  @Listen('resize', {target: 'window'})
  updateViewport() {
    this.isMobile = isMobile() || isSmallerThanDesktopViewport();
    this.toggleCardScrollbar();
    this.resizeContainer();
  }

  private nearestOffset(offset: number): number {
    return (Math.ceil(offset / this.itemWidth) * this.itemWidth);
  }

  private prev(): void  {
    this.logger.trace('CmsHorizontalTiles: prev');
    if (this.offset < 0) {
      if (this.lastOffset > 0) {
        this.offset += this.lastOffset;
        this.lastOffset = 0;
      } else {
        this.offset = Math.min((this.offset +this.itemWidth), 0);
      }
      this.updateTiles();
    }
  }

  private next(): void  {
    this.logger.trace('CmsHorizontalTiles: next');
    if (this.offset <= 0 && this.offset > this.maxOffset) {
      this.offset -= this.itemWidth;
      if (this.offset < this.maxOffset) {
        this.lastOffset = (this.offset - this.maxOffset + CmsHorizontalTiles.ITEM_GAP) * -1;
        this.offset = this.maxOffset;
      }
      this.updateTiles();
    }
  }

  private lock(event: TouchEvent): void {
    this.x0 = event.changedTouches[0].clientX;
    this.y0 = event.changedTouches[0].clientY;
  }

  private move(event: TouchEvent): void {
    let element = event.target as HTMLElement;
    if (element.tagName.toLowerCase()==='button' || element.tagName.toLowerCase()==='svg') return;
    this.logger.trace('CmsHorizontalTiles: move. ' + event.target);

    const dx = event.changedTouches[0].clientX - this.x0;
    const dy = event.changedTouches[0].clientY - this.y0;

    if((Math.abs(dx) > Math.abs(dy)) && (this.x0 || this.x0 === 0)) {
      const s = Math.sign(dx),
            offset = this.nearestOffset(Math.abs(dx)) * s;
      this.offset = Math.max(this.maxOffset, Math.min(0, this.offset + offset));
      this.updateTiles();
      this.x0 = null
    }
  }

  private updateTiles() {
    this.list.style.transform = `translate3d(${this.offset}px, 0px, 0px)`;
    this.buttonPrev.disabled = this.offset == 0;
    this.buttonNext.disabled = this.offset <= (this.maxOffset);
  }

  // Show or hide overlay, change button style
  private toggleOverlay(id: string): void {
    const overlay = this.host.shadowRoot.getElementById(id) as Element;
    const openClass = 'card-overlay-open';
    const isOpen = overlay.classList.contains(openClass);
    overlay.classList.toggle(openClass);
    if (!isOpen) {
      this.toggleCardScrollbar(overlay);
    }
  }

  private static getText(item: CmsHorizontalTile): string {
    const position = item.textPosition || 'top';
    return (
      <div class={`text text-${position}`}>
        <strong class="category">{item.category}</strong>
        <strong class="headline">{item.headline}</strong>
        <p innerHTML={item.text} />
      </div>
    );
  }

  private static getImagePosition(textPosition: string): string {
    if (textPosition == "bottom") {
      return "top";
    }
    else if (textPosition == "center") {
      return textPosition;
    }

    return "bottom";
  }

  render() {
    const additionalStyles = { height: 'auto' };
    return (
      <Host class={this.sectionBehavior.hostClass}>
        <section
          style={{ ...this.sectionBehavior.sectionStyle, ...additionalStyles }}
          class={this.sectionBehavior.sectionClass}
          onTouchStart={this.lock.bind(this)}
          onTouchEnd={this.move.bind(this)}
        >
          { renderBlockMargin(this.sectionBehavior, VerticalPositionTop) }
          <div class="section-inner wrapper">
            <div class="sticky-wrap">
              <div class="sticky-inner" ref={(divElement: HTMLDivElement) => (this.container = divElement)}>
                <div class="section-headline-wrapper">
                  {this.options.headline ? <h3>{this.options.headline}</h3> : ''}
                  {this.options.subheadline ? <h4>{this.options.subheadline}</h4> : ''}
                </div>
                <div class="tiles" ref={(divElement: HTMLDivElement) => (this.tilesContainer = divElement)}>
                  <div class="tiles-inner" ref={(divElement: HTMLDivElement) => (this.tilesInner = divElement)}>
                    <ul role="list" class="cards" ref={(element: HTMLUListElement) => (this.list = element)}>
                      {
                        this.options.items.map((item, index) => (
                          <li role="listitem" id={`card-${index}`}
                              style={{ color: item.textColor ? item.textColor : CmsHorizontalTiles.TEXT_COLOR }}>
                            <div class="card"
                                 style={{ backgroundColor: item.backgroundColor ? item.backgroundColor : CmsHorizontalTiles.BACKGROUND_COLOR }}>
                              { item.textPosition == null || item.textPosition == 'top' ? CmsHorizontalTiles.getText(item) : '' }

                              <div class={`image-wrapper image-${CmsHorizontalTiles.getImagePosition(item.textPosition)}`}>
                                <img src={item.picture} alt={item.pictureAlt} />
                                { item.textPosition == 'center' ? CmsHorizontalTiles.getText(item) : '' }
                              </div>

                              { item.textPosition == 'bottom' ? CmsHorizontalTiles.getText(item) : '' }

                              {
                                item.content
                                  ? <div class="card-overlay">
                                      <label tabIndex={0} class="card-button-wrapper">
                                        <span class={`card-button`}
                                              onClick={() => this.toggleOverlay('card-'+index)}>
                                          <svg class="card-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                            <path d="M18.5,8.51h-7v-7A1.5,1.5,0,0,0,10,0h0A1.5,1.5,0,0,0,8.5,1.5v7h-7a1.5,1.5,0,0,0,0,3h7v7A1.5,1.5,0,0,0,10,20h0a1.5,1.5,0,0,0,1.5-1.5v-7h7a1.5,1.5,0,0,0,0-3Z" />
                                          </svg>
                                        </span>
                                        <span class="card-button-text" role="button" aria-expanded="false" aria-controls="content-toggle-watch">
                                          <span class="visualHidden">{item.buttonAlt}</span>
                                        </span>
                                    </label>
                                    <div class={`card-overlay-content`} role="group"
                                         aria-label={`${this.options.moreContentPrefix} „${item.headline}“`} aria-hidden="true">
                                      <div class={`card-overlay-content-wrapper`} innerHTML={item.content} />
                                    </div>
                                  </div>
                                  : ''
                              }
                            </div>
                          </li>
                        ))
                      }
                    </ul>
                  </div>
                </div>
                <div class={ `btn-wrapper`} ref={(divElement: HTMLDivElement) => (this.buttonContainer = divElement)}>
                  <button
                    aria-label="Previous"
                    disabled={true}
                    onClick={this.prev.bind(this)}
                    class="btn-prev button"
                    ref={(element: HTMLButtonElement) => (this.buttonPrev = element)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path d="M15.293 3.293 6.586 12l8.707 8.707 1.414-1.414L9.414 12l7.293-7.293-1.414-1.414z"/></svg>
                  </button>
                  <button
                    aria-label="Next"
                    onClick={this.next.bind(this)}
                    class="btn-next button"
                    ref={(element: HTMLButtonElement) => (this.buttonNext = element)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"><path d="M7.293 4.707 14.586 12l-7.293 7.293 1.414 1.414L17.414 12 8.707 3.293 7.293 4.707z"/></svg>
                  </button>
                </div>

              </div>
            </div>
          </div>
          { renderBlockMargin(this.sectionBehavior, VerticalPositionBottom) }
        </section>
      </Host>
    );
  }
}

interface CmsHorizontalTile {
  category: string;
  headline: string;
  text: string;
  picture: string;
  pictureAlt?: string;
  buttonAlt?: string;
  content: string;
  textPosition?: TextPosition;
  textColor?: string;
  backgroundColor?: string;
}

interface CmsHorizontalTilesConfig {
  headline?: string;
  subheadline?: string;
  items?: CmsHorizontalTile[];
  moreContentPrefix: string;
}
